import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import InputControlled from '../ui/InputControlled';
import { emptyOption } from '../ui/InputOptions';
import SelectControlled from '../ui/SelectControlled';
import InputCheckControlled from '../ui/InputCheckControlled';
import useResource from '../../api/services/useResource';
import { useFormContext } from 'react-hook-form';
import { getBrands } from '../../redux/actions/brand';
import { getVersionsByModel } from '../../redux/actions/version';
import { getModelsByBrand } from '../../redux/actions/model';

const FormHeader = () => {
  const { watch } = useFormContext();
  const dispatch = useDispatch();
  const { data: providers } = useResource({ resource: '/api/providers' });
  const { data: categories } = useResource({ resource: '/api/categories' });
  const { data: colors } = useResource({ resource: '/api/v1/color' });

  // Get brands from redux store
  const brandsState = useSelector((state) => state.brand.get);
  const brands = brandsState?.data || [];

  // State for models and versions
  const [models, setModels] = useState([]);
  const [versions, setVersions] = useState([]);
  const modelsState = useSelector((state) => state.model.data);
  const versionsState = useSelector((state) => state.version.data);

  // Load brands on component mount
  useEffect(() => {
    dispatch(getBrands());
  }, [dispatch]);

  const providersOptions =
    providers?.providers?.map((provider) => {
      return {
        label: provider.name,
        value: provider.name,
      };
    }) ?? [];
  const categoriesOptions =
    categories?.data?.map((category) => ({
      value: category.category_name,
      label: category.category_name,
    })) ?? [];
  const colorsOptions =
    colors?.data?.map((color) => ({
      value: color.name,
      label: color.name,
    })) ?? [];

  // Brand options
  const brandsOptions = brands.map((brand) => ({
    value: brand.name,
    label: brand.name,
  }));

  // Model options based on selected brand
  const selectedBrand = watch('head.marca')?.value;
  const selectedBrandData = brands.find(
    (brand) => brand.name === selectedBrand,
  );

  // Get brand ID for API call
  const selectedBrandId = selectedBrandData?.id;

  // Load models when brand changes
  useEffect(() => {
    if (selectedBrandId) {
      dispatch(getModelsByBrand(selectedBrandId));
    } else {
      setModels([]);
    }
  }, [selectedBrandId, dispatch]);

  // Update models when state changes
  useEffect(() => {
    if (modelsState?.data) {
      setModels(modelsState.data);
    }
  }, [modelsState]);

  const modelsOptions = models.map((model) => ({
    value: model.name,
    label: model.name,
  }));

  // Version options based on selected model
  const selectedModel = watch('head.modelo')?.value;
  const selectedModelData = models.find(
    (model) => model.name === selectedModel,
  );

  // Get model ID for API call
  const selectedModelId = selectedModelData?.id;

  // Load versions when model changes
  useEffect(() => {
    if (selectedModelId) {
      dispatch(getVersionsByModel(selectedModelId));
    } else {
      setVersions([]);
    }
  }, [selectedModelId, dispatch]);

  // Update versions when state changes
  useEffect(() => {
    if (versionsState?.data) {
      setVersions(versionsState.data);
    }
  }, [versionsState]);

  const versionsOptions = versions.map((version) => ({
    value: version.name,
    label: version.name,
  }));

  const categoryValue = watch('head.categoria')?.value;
  const categoriesTypeOptions =
    (categoryValue &&
      categories?.data
        ?.find((item) => item.category_name === categoryValue)
        ?.types?.map((type) => ({
          value: type.name,
          label: type.name,
        }))) ??
    [];

  // Year options: from next year down to 1950
  const startYear = new Date().getFullYear() + 1;
  const yearOptions = Array.from(
    { length: startYear - 1950 + 1 },
    (_, index) => {
      const yearString = String(startYear - index);
      return { value: yearString, label: yearString };
    },
  );

  // Validation helpers
  const isIntegerOrEmpty = (fieldValue) => {
    const stringValue = String(fieldValue?.value ?? '');
    return (
      stringValue === '' ||
      /^\d+$/.test(stringValue) ||
      'Debe ser un número entero'
    );
  };

  const isDecimalOrEmpty = (fieldValue) => {
    const stringValue = String(fieldValue?.value ?? '');
    return (
      stringValue === '' ||
      /^(?:\d+)(?:,\d+)?$/.test(stringValue) ||
      'Debe ser un número decimal con coma'
    );
  };

  const validateDocumentField = (fieldValue) => {
    // Validate the complete field structure, focusing on the obs subfield
    if (!fieldValue || !fieldValue.value) return true;

    const obsValue = fieldValue.value.obs;
    if (!obsValue || obsValue === '') return true;

    return /^[\d-]+$/.test(obsValue) || 'Solo se permiten números y guiones';
  };

  const validatePatenteOrEmpty = (fieldValue) => {
    const raw = String(fieldValue?.value ?? '').trim();
    if (raw === '') return true;

    const value = raw.toUpperCase();
    const formato1 = /^[A-Z]{2}\d{4}$/; // 2 letters + 4 digits
    const formato2Strict = /^[B-DF-HJ-NP-TV-Z]{4}\d{2}$/; // 4 consonants (no AEIOU) + 2 digits
    const formato2Loose = /^[A-Z]{4}\d{2}$/; // 4 letters + 2 digits (allows vowels)

    if (formato1.test(value)) return true;
    if (formato2Strict.test(value)) return true;
    if (formato2Loose.test(value)) return 'Contiene vocales en formato 2';
    return 'Formato incorrecto';
  };

  return (
    <div>
      <div>
        <div className="">
          <SelectControlled
            name={`head.mandante`}
            options={[emptyOption, ...providersOptions]}
            rules={{ required: true }}
          />
        </div>
        <div className="mt-2">
          <SelectControlled
            name={`head.categoria`}
            options={[emptyOption, ...categoriesOptions]}
            rules={{ required: true }}
          />
        </div>
        <div className="mt-2">
          <SelectControlled
            name={`head.tipo`}
            options={[emptyOption, ...categoriesTypeOptions]}
            rules={{ required: true }}
          />
        </div>
      </div>
      <div className="row mt-4">
        <div className={'col-md-5'}>
          <h3>Identificacion del Bien</h3>
          <div className="mb-2">
            <SelectControlled
              name={`head.marca`}
              options={[emptyOption, ...brandsOptions]}
              rules={{ required: true }}
            />
          </div>
          <div className="mb-2">
            <SelectControlled
              name={`head.modelo`}
              options={[emptyOption, ...modelsOptions]}
              rules={{ required: true }}
            />
          </div>
          <div className="mb-2">
            <SelectControlled
              name={`head.version`}
              options={[emptyOption, ...versionsOptions]}
            />
          </div>
          <div className="mb-2">
            <SelectControlled
              name={`head.ano`}
              options={[emptyOption, ...yearOptions]}
            />
          </div>

          <div className="mb-2">
            <SelectControlled
              name={`head.color`}
              options={[emptyOption, ...colorsOptions]}
            />
          </div>
          <InputControlled
            name={`head.patente`}
            rules={{ validate: validatePatenteOrEmpty }}
          />
          <InputControlled name={`head.motor`} />
          <InputControlled name={`head.chasis`} />
          <InputControlled
            name={`head.kms`}
            rules={{ validate: isIntegerOrEmpty }}
          />
          <InputControlled
            name={`head.horas`}
            rules={{ validate: isDecimalOrEmpty }}
          />
          <InputControlled
            name={`head.codigo`}
            rules={{ validate: isIntegerOrEmpty }}
          />
        </div>
        <div className={'col-md-7'}>
          <h3>Documentos</h3>
          <InputCheckControlled
            name={`head.permiso`}
            rules={{ validate: validateDocumentField }}
          />
          <InputCheckControlled
            name={`head.revision`}
            rules={{ validate: validateDocumentField }}
          />
          <InputCheckControlled
            name={`head.certificacion`}
            rules={{ validate: validateDocumentField }}
          />
          <InputCheckControlled
            name={`head.seguro`}
            rules={{ validate: validateDocumentField }}
          />
        </div>
      </div>
    </div>
  );
};
export default FormHeader;
